import './sparkline.scss';

interface SparklineProps {
    data: number[];
    width?: number;
    height?: number;
    variant?: 'healthy' | 'warning' | 'critical' | 'neutral';
    tooltip?: string;
}

const PADDING = 2;

function Sparkline(props: SparklineProps) {
    const { data, width = 72, height = 22, variant = 'neutral', tooltip } = props;

    if (data.length === 0) {
        return <span className="sparkline-empty">&ndash;</span>;
    }

    // The y-axis is anchored at zero rather than the series minimum so that a flat
    // line sits on the baseline and rows stay visually comparable to each other.
    const peak = Math.max(...data);
    const scale = peak > 0 ? peak : 1;
    const plotHeight = height - PADDING * 2;
    const stepX = data.length > 1 ? width / (data.length - 1) : 0;

    const points = data.map((value, index) => {
        const x = data.length > 1 ? index * stepX : width / 2;
        const y = height - PADDING - (value / scale) * plotHeight;
        return { x, y };
    });

    const line = points.map(point => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');
    const area = `M ${points[0].x.toFixed(1)},${points[0].y.toFixed(1)} `
        + points.slice(1).map(point => `L ${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ')
        + ` L ${points[points.length - 1].x.toFixed(1)},${height} L ${points[0].x.toFixed(1)},${height} Z`;

    return (
        <svg className={`sparkline ${variant}`}
             width={width}
             height={height}
             viewBox={`0 0 ${width} ${height}`}
             preserveAspectRatio="none"
             role="img"
             aria-label={tooltip ?? 'Trend over the selected period'}
             data-tooltip-id={tooltip ? 'global-tooltip' : undefined}
             data-tooltip-content={tooltip}>
            <path className="sparkline-area" d={area}/>
            <polyline className="sparkline-line" points={line}/>
            <circle className="sparkline-marker"
                    cx={points[points.length - 1].x}
                    cy={points[points.length - 1].y}
                    r={1.8}/>
        </svg>
    );
}

// Element-wise total of several series, used to roll a replica's databases up
// into one line. Series are the same length, coming from one bucketed query.
export const sumSeries = (seriesList: number[][]): number[] => {
    if (seriesList.length === 0) return [];
    return seriesList[0].map((_, index) =>
        Math.round(seriesList.reduce((total, series) => total + (series[index] ?? 0), 0) * 10) / 10);
};

export default Sparkline;
